import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError, Provider } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { identifyUser, resetAnalyticsUser } from '../lib/analytics';
import { disablePushNotifications } from '../lib/pushNotifications';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, inviteCode?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithProvider: (provider: Provider) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if the URL contains OAuth callback params (hash fragments or PKCE code)
    // If so, don't mark loading complete from getSession — wait for onAuthStateChange
    const hasAuthCallback =
      window.location.hash.includes('access_token') ||
      new URLSearchParams(window.location.search).has('code');

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Only mark loading done if there's no OAuth callback pending,
      // or if we already have a session
      if (!hasAuthCallback || session) {
        setLoading(false);
      }
    });

    // Listen for auth changes — this handles OAuth callbacks
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        identifyUser(session.user.id, { email: session.user.email });
      } else {
        resetAnalyticsUser();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, inviteCode?: string) => {
    const redirectUrl = inviteCode
      ? `https://deadbird.app?pending_invite=${encodeURIComponent(inviteCode)}`
      : 'https://deadbird.app';
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithProvider = async (provider: Provider) => {
    const redirect = new URL('https://deadbird.app');
    try {
      const pending = JSON.parse(localStorage.getItem('pendingInvite') || 'null');
      if (typeof pending?.code === 'string') redirect.searchParams.set('pending_invite', pending.code);
    } catch { /* Ignore malformed saved invites. */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirect.toString(),
      },
    });
    return { error };
  };

  const signOut = async () => {
    await disablePushNotifications().catch(() => undefined);
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithProvider,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
